import { CircularProgress, SvgIcon } from '@mui/material'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search-small.svg'
import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import Button from '../button/button.component'
import { BUTTON_VARIANTS } from '../../../components/shared/button/button.constants'
import TextField from '../../../components/shared/text-field/text-field.component'
import { useDebounce } from '../../../hooks/useDebounce'

import { SEARCH_DEBOUNCE, SEARCH_FIELD_NAME, uuid } from './search.constants'
import { styles, handleStylesWithProps } from './search.styles'

const Search = forwardRef(
  (
    {
      initialValue,
      searchCallback,
      placeholder,
      debounceValue,
      sx,
      iconSx,
      onKeyDown,
      onSearchIconClick,
      onClear,
      searchLoader,
      isDisabled,
      endAdornment,
      searchIconPosition,
      inputPropsSx,
      hasToolbarWithBorder,
      showSearchFieldBorder,
    },
    ref
  ) => {
    const formMethods = useForm({
      defaultValues: { search: initialValue },
      mode: 'all',
    })
    const [isFocused, setIsFocused] = useState(false)
    const { watch, setValue } = formMethods
    const searchValue = watch(SEARCH_FIELD_NAME)
    const hasClearButton = searchValue?.length > 0
    const debouncedSearchValue = useDebounce(searchValue, debounceValue)
    const handleClearSearchField = useCallback(() => {
      setValue(SEARCH_FIELD_NAME, '')
    }, [setValue])

    useEffect(() => {
      if (!initialValue) {
        handleClearSearchField()
      }
    }, [initialValue, handleClearSearchField])

    useImperativeHandle(ref, () => ({
      handleClearSearchField,
    }))

    // Clear internal state when onClear ref is provided
    useEffect(() => {
      if (onClear && typeof onClear === 'object' && 'current' in onClear) {
        onClear.current = handleClearSearchField
      }
    }, [onClear, handleClearSearchField])

    useEffect(() => {
      if (searchValue === '' || debouncedSearchValue === searchValue) {
        searchCallback(searchValue === '' ? '' : debouncedSearchValue)
      }
    }, [debouncedSearchValue, searchValue, searchCallback])

    const stylesWithProps = handleStylesWithProps({
      sx,
      isDisabled,
      hasToolbarWithBorder,
      showSearchFieldBorder,
    })

    return (
      <FormProvider {...formMethods}>
        <div style={styles.formTag}>
          <TextField
            style={styles.searchField}
            disabled={isDisabled}
            id={placeholder}
            sx={stylesWithProps.container}
            name={SEARCH_FIELD_NAME}
            onKeyPress={(e) => {
              e.key === 'Enter' && e.preventDefault()
            }}
            placeholder={placeholder}
            InputProps={{
              sx: inputPropsSx,
              onFocus: () => setIsFocused(true),
              startAdornment: onSearchIconClick ? (
                <Button
                  variant={BUTTON_VARIANTS.ICON}
                  icon={SearchIcon}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onSearchIconClick(e)
                  }}
                  type="button"
                  disabled={isDisabled}
                  sx={styles.searchIconButton}
                />
              ) : (
                searchIconPosition === 'start' && (
                  <SvgIcon
                    component={SearchIcon}
                    sx={{
                      ...styles.searchIcon,
                      ...(isDisabled && styles.disabledSearchIcon),
                      ...iconSx,
                    }}
                  />
                )
              ),
              endAdornment: (
                <>
                  {searchIconPosition === 'end' && (
                    <SvgIcon
                      component={SearchIcon}
                      sx={{
                        ...styles.searchIcon,
                        ...(isDisabled && styles.disabledSearchIcon),
                      }}
                    />
                  )}
                  {searchLoader ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      {hasClearButton && isFocused && (
                        <Button
                          variant={BUTTON_VARIANTS.ICON}
                          icon={ClearSearchIcon}
                          onClick={handleClearSearchField}
                          sx={styles.clearSearchIcon}
                        />
                      )}
                    </>
                  )}
                </>
              ),
            }}
            uid={uuid ? `${uuid}-${SEARCH_FIELD_NAME}` : ''}
            {...(onKeyDown && { onKeyDown })}
            hideHelperText
          />
          {endAdornment}
        </div>
      </FormProvider>
    )
  }
)

Search.propTypes = {
  initialValue: PropTypes.string,
  searchCallback: PropTypes.func,
  placeholder: PropTypes.string,
  debounceValue: PropTypes.number,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  iconSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  onKeyDown: PropTypes.func,
  onSearchIconClick: PropTypes.func,
  onClear: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.func })]),
  searchLoader: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasToolbarWithBorder: PropTypes.bool,
  showSearchFieldBorder: PropTypes.bool,
  endAdornment: PropTypes.node,
  searchIconPosition: PropTypes.oneOf(['start', 'end']),
  inputPropsSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
}

Search.defaultProps = {
  initialValue: '',
  searchCallback: () => {},
  placeholder: '',
  debounceValue: SEARCH_DEBOUNCE,
  sx: {},
  searchLoader: false,
  searchIconPosition: 'start',
  showSearchFieldBorder: true,
}

export default React.memo(Search)
